import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { Network, Loader2, Target, Award } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt$ = (n) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : `$${(n || 0).toLocaleString()}`;

export default function BrokerDashboard() {
  const { user, loading, authHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/brokers/me`, { headers: authHeaders(), withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || 'No broker profile'));
  // eslint-disable-next-line
  }, [user]);

  if (loading) return <div className="px-6 md:px-24 py-32 text-center text-[#A3A3A3]">Verifying...</div>;
  if (!user) return <Navigate to="/login" replace />;

  if (err) {
    return (
      <div className="px-6 md:px-12 lg:px-24 py-24">
        <Network size={40} className="text-[#D4AF37] mb-8" strokeWidth={1} />
        <h1 className="text-4xl font-extralight mb-4">No broker profile linked.</h1>
        <p className="text-[#A3A3A3] mb-8">Apply to join the Sovereign Broker Network from the Broker Portal.</p>
        <a href="/broker-portal" className="btn-sovereign"><span>Apply</span></a>
      </div>
    );
  }
  if (!data) return <div className="px-6 md:px-24 py-32 text-center"><Loader2 size={20} className="animate-spin text-[#D4AF37]" /></div>;

  const { broker, leads, metrics, lineage_law } = data;

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16 min-h-[80vh]">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
        <div className="md:col-span-7">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6 flex items-center gap-3">
            <Network size={14} strokeWidth={1.5} /> Broker Sub-Dashboard
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight leading-[1] tracking-tight">
            {broker.full_name.split(' ')[0]},<br />
            <span className="font-display italic text-[#D4AF37]">{broker.state} anchor.</span>
          </h1>
        </div>
        <div className="md:col-span-5 flex items-end">
          <div className="text-sm text-[#A3A3A3] font-light leading-relaxed">
            <div className="mb-2">Status: <span className="text-[#D4AF37]">{broker.status}</span></div>
            <div>{broker.years_experience} years · {broker.brokerage || 'Independent'}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 border-t border-b border-[#D4AF37]/15 py-8">
        <Stat label="Leads Assigned" value={metrics.leads_assigned} />
        <Stat label="Pursuing" value={metrics.leads_pursuing} />
        <Stat label="Acquired" value={metrics.leads_acquired} accent />
        <Stat label="Pipeline" value={fmt$(metrics.pipeline_value_usd)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6 flex items-center gap-2">
            <Target size={12} strokeWidth={1.5} /> Assigned Leads
          </div>
          {leads.length === 0 ? (
            <div className="sov-border bg-[#0A0A0A] p-12 text-center text-sm text-[#A3A3A3] font-light">
              No leads assigned. Mercy's Scouter will dispatch high-ROI leads to your dashboard as they surface.
            </div>
          ) : (
            <div className="space-y-3">
              {leads.map((l) => (
                <div key={l.id} className="sov-border bg-[#0A0A0A] p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-1">{l.source}</span>
                        <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{l.country}</span>
                      </div>
                      <div className="text-sm font-light mb-1">{l.address}</div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">{l.location}</div>
                      <p className="text-xs text-[#A3A3A3] italic font-light">"{l.intel}"</p>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-3xl text-[#D4AF37]">{l.lead_score}</div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mt-1">{l.status}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <aside className="lg:col-span-4">
          <div className="sov-border bg-[#0A0A0A] p-6 mb-6">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4 flex items-center gap-2">
              <Award size={12} strokeWidth={1.5} /> Lineage Law · Broker
            </div>
            <div className="space-y-3">
              <Row l="Your slice" v={`${lineage_law.broker_split_pct}% of Admiral pool`} />
              <Row l="Admiral · Mercy" v={`${lineage_law.admiral_pct}%`} />
              <Row l="Operations" v={`${lineage_law.operations_pct}%`} />
              <Row l="CyberKids" v={`${lineage_law.cyberkids_pct}%`} />
            </div>
          </div>
          <div className="sov-border bg-[#0A0A0A] p-6">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4">Specialties</div>
            <div className="flex flex-wrap gap-2">
              {broker.specialties?.length > 0 ? broker.specialties.map((s) => (
                <span key={s} className="text-[9px] tracking-[0.2em] uppercase border border-[#D4AF37]/30 text-[#A3A3A3] px-2 py-1">{s}</span>
              )) : <span className="text-xs text-[#A3A3A3]">None declared</span>}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div>
    <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">{label}</div>
    <div className={`metric-number text-3xl md:text-4xl ${accent ? 'text-[#D4AF37]' : ''}`}>{value}</div>
  </div>
);

const Row = ({ l, v }) => (
  <div className="flex justify-between text-sm border-b border-[#D4AF37]/15 pb-2 last:border-b-0">
    <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{l}</span>
    <span className="font-light text-[#F5F5F5]">{v}</span>
  </div>
);
